import { Theme, ThemeUIStyleObject } from 'theme-ui';

import { IconName, icons } from '../components/Icon';
import { sortAscending } from '../helpers/sort';

import { computeButtonColors } from './variants';

/**
 * Figmagic integration: import Design Tokens from a Figma document to feed the theme
 * @see https://github.com/mikaelvesavuori/figmagic
 */
import BorderWidthTokens from '../tokens/borderWidths';
import ColorTokens from '../tokens/colors';
import FontFamilyTokens from '../tokens/fontFamilies';
import FontSizeTokens from '../tokens/fontSizes';
import FontWeightTokens from '../tokens/fontWeights';
import LetterSpacingTokens from '../tokens/letterSpacings';
import LineHeightTokens from '../tokens/lineHeights';
import MediaQueryTokens from '../tokens/mediaQueries';
import OpacityTokens from '../tokens/opacities';
import RadiiTokens from '../tokens/radii';
import ShadowTokens from '../tokens/shadows';
import SpacingTokens from '../tokens/spacing';
import ZIndiceTokens from '../tokens/zIndices';

export interface SchedulingThemeType extends Theme {
  // theme UI works with an array of breakpoints
  // https://theme-ui.com/theme-spec/#breakpoints
  breakpoints: string[];
  borderWidths: typeof BorderWidthTokens;
  colors: typeof ColorTokens & {
    [key: string]: string;
  };
  // fonts: typeof FontFamilyTokens;
  fontSizes: typeof FontSizeTokens;
  fontWeights: typeof FontWeightTokens;
  letterSpacings: typeof LetterSpacingTokens;
  lineHeights: typeof LineHeightTokens;
  opacities: typeof OpacityTokens;
  radii: typeof RadiiTokens;
  space: typeof SpacingTokens;
  shadows: typeof ShadowTokens;
  sizes: {
    container: number;
  };
  zIndices: typeof ZIndiceTokens;
  icons: IconName[];
  // new theme keys
  circles?: Record<string, ThemeUIStyleObject>;
}

/**
 * Custom Theme object for theme-ui
 * Contains design tokens and centralised components variants
 * @see https://theme-ui.com/theming
 *
 * @note ThemeUI TypeScript support is WIP at the moment
 * This is why we need to make unfortunate assertions on the types for now
 *
 * @var object
 */
const SchedulingTheme: SchedulingThemeType = {
  config: {
    useBorderBox: true,
    useColorSchemeMediaQuery: false,
    useLocalStorage: false,
    useRootStyles: true
  },
  // -------------------------------------------------- //
  // Design Tokens                                      //
  // Define values or import from figmagic tokens files //
  // https://theme-ui.com/theme-spec                    //
  // -------------------------------------------------- //
  /**
   * responsive breakpoints (mobile-first):
   * @see https://theme-ui.com/getting-started/#responsive-styles
   */
  breakpoints: Object.values<string>(MediaQueryTokens).sort(sortAscending),
  /**
   * colors
   * @see https://theme-ui.com/theming#colors
   */
  colors: {
    ...ColorTokens,
    // Following keys are part of the Theme Specifications from theme-ui
    text: ColorTokens.veloDarkBlue,
    background: ColorTokens.veloGrey1,
    primary: ColorTokens.veloMainBlue,
    secondary: ColorTokens.veloPeach,
    accent: ColorTokens.veloDarkBlue,
    border: ColorTokens.veloGrey2,
    muted: ColorTokens.veloBlueGrey,
    highlight: ColorTokens.veloMainBlue
  },
  /**
   * typography
   * @see https://theme-ui.com/theming#typography
   */
  fonts: {
    body: `"${FontFamilyTokens.body}", sans-serif`,
    heading: `"${FontFamilyTokens.heading}", sans-serif`,
    monospace: `"${FontFamilyTokens.monospace}", monospace`
  },
  fontSizes: FontSizeTokens,
  fontWeights: FontWeightTokens,
  letterSpacings: LetterSpacingTokens,
  lineHeights: LineHeightTokens,
  opacities: OpacityTokens,
  /**
   * theme scales
   * @see https://theme-ui.com/theme-spec#theme-scales
   */
  borderWidths: BorderWidthTokens,
  radii: RadiiTokens,
  space: SpacingTokens,
  sizes: {
    container: 1440
  },
  shadows: ShadowTokens,
  zIndices: ZIndiceTokens,
  // Icon names
  icons,
  // --------------------------------------------------- //
  // Components Variants                                 //
  // Compose for specific components and/or reuse styles //
  // https://theme-ui.com/components                     //
  // --------------------------------------------------- //
  // alerts: {},
  buttons: {
    default: {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontFamily: 'body',
      outline: 'none',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      transition: 'all 0.3s ease-in-out',
      '&:disabled': {
        opacity: 'dim',
        cursor: 'not-allowed'
      }
    },
    primary: {
      variant: 'buttons.default',
      px: 'm',
      py: 's',
      borderRadius: 'rounded',
      borderWidth: 'borderless',
      boxShadow: 'medium',
      textTransform: 'uppercase',
      fontSize: 'button',
      fontWeight: 'button',
      lineHeight: 'button',
      letterSpacing: 'button'
    },
    primaryDarkBlue: computeButtonColors('primary', 'veloDarkBlue', 'veloPeach'),
    /** @deprecated use "primaryDarkBlue" instead (or check with designers which variant to use) */
    primaryShiftBlue: computeButtonColors('primary', 'veloDarkBlue', 'veloPeach'),
    primarySystemGreen: computeButtonColors('primary', 'systemGreen', 'text'),
    /** @deprecated use "primaryDarkBlue" instead */
    primaryDarkBlueWhite: computeButtonColors('primary', 'veloDarkBlue', 'veloPeach'),
    outline: {
      variant: 'buttons.default',
      px: 'm',
      py: 's',
      borderRadius: 'rounded',
      border: 'solid',
      borderWidth: 'regular',
      boxShadow: 'medium',
      textTransform: 'uppercase',
      fontSize: 'button',
      fontWeight: 'button',
      lineHeight: 'button',
      letterSpacing: 'button'
    },
    outlineSystemBlueLight: computeButtonColors('outline', 'systemBlueLight', 'text', {
      '&:enabled:hover': {
        backgroundColor: 'systemBlueLight'
      }
    }),
    outlineSystemWarning: computeButtonColors('outline', 'systemWarning', 'systemWarning', {
      '&:enabled:hover': {
        backgroundColor: 'systemWarningHover',
        color: 'veloWhite'
      }
    }),
    rounded: {
      variant: 'buttons.default',
      px: 's',
      py: '0.75em',
      borderRadius: 'circular',
      borderWidth: 'borderless',
      boxShadow: 'none',
      fontSize: 'buttonS',
      fontWeight: 'buttonS',
      lineHeight: 'buttonS',
      letterSpacing: 'buttonS'
    },
    roundedDarkBlue: computeButtonColors('rounded', 'veloDarkBlue', 'veloPeach'),
    /** @deprecated use "roundedDarkBlue" instead (or check with designers which variant to use) */
    roundedShiftBlue: computeButtonColors('rounded', 'veloDarkBlue', 'veloPeach'),
    roundedSystemWarning: computeButtonColors('rounded', 'systemWarning', 'veloWhite'),
    roundedOutlineGrey: computeButtonColors('rounded', 'transparent', 'veloBlueGrey', {
      borderColor: 'veloGrey2',
      borderWidth: 'regular',
      borderStyle: 'solid',
      '&:enabled:hover': {
        backgroundColor: 'veloGrey1'
      }
    }),
    roundedOutlineSystemWarning: computeButtonColors('rounded', 'transparent', 'systemWarning', {
      borderColor: 'systemWarning',
      borderWidth: 'regular',
      borderStyle: 'solid',
      '&:enabled:hover': {
        backgroundColor: 'systemWarning',
        color: 'veloWhite'
      }
    }),
    text: {
      variant: 'buttons.default',
      p: 0, // icon will be setting the height
      borderRadius: 'light',
      borderWidth: 'borderless',
      boxShadow: 'none',
      fontSize: 'buttonS',
      fontWeight: 'buttonS',
      // Those need an adjusted line-height since they might have an icon
      lineHeight: 'button',
      letterSpacing: 'buttonS'
    },
    textDarkBlue: computeButtonColors('text', 'veloDarkBlue'),
    textSystemWarning: computeButtonColors('text', 'systemWarning'),
    icon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 40,
      height: 40,
      p: 0,
      border: 0,
      borderRadius: 'rounded',
      outline: 'none',
      color: 'systemBlueLight',
      backgroundColor: 'transparent',
      transition: '0.3s',
      '&:not(:last-child)': {
        mr: 'xs'
      },
      '&:enabled': {
        cursor: 'pointer',
        '&:hover': {
          color: 'veloShiftBlue'
        },
        '&:active,&.active': {
          color: 'veloWhite',
          backgroundColor: 'veloShiftBlue'
        }
      },
      '&:disabled': {
        opacity: 'dim'
      }
    }
  },
  cards: {
    white: {
      backgroundColor: 'veloWhite',
      borderRadius: 'rounded',
      boxShadow: 'medium'
    }
  },
  circles: {
    default: {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 40,
      height: 40,
      borderStyle: 'solid',
      borderColor: 'veloGrey2',
      borderWidth: 'regular',
      borderRadius: 'circular'
    }
  },
  forms: {
    label: {
      variant: 'text.bodySemibold',
      fontSize: 'bodyS',
      mb: 1,
      pl: 's'
    },
    default: {
      variant: 'text.bodyMedium',
      p: 's',
      backgroundColor: 'veloWhite',
      borderColor: 'veloGrey2',
      borderRadius: 'rounded',
      outline: 'none',
      boxShadow: 'medium',
      '&:focus': {
        backgroundColor: 'veloGrey3',
        borderColor: 'veloBlueGrey'
      }
    },
    checkbox: {
      border: 'solid',
      borderWidth: 'regular',
      borderColor: 'veloGrey2',
      borderRadius: 3,
      color: 'transparent',
      backgroundColor: 'veloGrey1',
      '&:hover, &:active, &:focus, input:focus ~ &': {
        backgroundColor: 'veloGrey3'
      },
      'input:checked ~ &': {
        color: 'veloShiftBlue'
      },
      'input:disabled ~ &': {
        cursor: 'not-allowed',
        opacity: 'dim'
      }
    },
    input: {
      variant: 'forms.default'
    },
    radio: {
      border: 'solid',
      borderWidth: 'regular',
      borderColor: 'veloGrey2',
      borderRadius: 'circular',
      color: 'transparent',
      backgroundColor: 'veloGrey1',
      '&:hover, &:active, &:focus, input:focus ~ &': {
        backgroundColor: 'veloGrey3'
      },
      'input:checked ~ &': {
        color: 'veloShiftBlue'
      },
      'input:disabled ~ &': {
        cursor: 'not-allowed',
        opacity: 'dim'
      }
    },
    select: {
      variant: 'forms.default'
    },
    slider: {
      borderRadius: 'rounded'
    },
    switch: {
      width: 48,
      height: 32,
      borderRadius: 16,
      backgroundColor: 'veloGrey2',
      '& > div': {
        // currently switch's padding cannot be customised
        m: 1,
        width: 26,
        height: 26,
        boxShadow: `0 4px 8px ${ColorTokens.veloGrey2}`
      },
      'input:checked ~ &': {
        bg: 'veloShiftBlue',
        '> div': {
          // 16px: ({switch width: 48} - {switch toggle width: 26} - {switch paddingX: 6})
          boxShadow: `0 4px 8px ${ColorTokens.veloShiftBlue}`,
          transform: 'translateX(16px)'
        }
      }
    },
    textarea: {
      variant: 'forms.default',
      minHeight: 80
    },
    noShadow: {
      variant: 'forms.default',
      boxShadow: 'none'
    },
    invalid: {
      variant: 'text.bodyMedium',
      p: 's',
      backgroundColor: 'veloGrey3',
      color: 'systemWarning',
      borderColor: 'systemWarning',
      borderRadius: 'rounded',
      outline: 'none',
      boxShadow: 'medium'
    },
    invalidNoShadow: {
      variant: 'forms.invalid',
      boxShadow: 'none'
    }
  },
  layout: {
    container: {
      px: 's'
    }
  },
  links: {
    nav: {
      appearance: 'none',
      display: 'inline-flex',
      my: 's',
      p: 0,
      backgroundColor: 'transparent',
      color: 'text',
      border: 'solid',
      borderWidth: 'regular',
      borderColor: 'veloGrey2',
      borderRadius: 'circular',
      outline: 'none',
      transition: '0.3s',
      cursor: 'pointer',
      '&.active': {
        backgroundColor: 'text',
        borderColor: 'text',
        color: 'veloShiftBlue'
      }
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 48,
      height: 48,
      border: 0,
      borderRadius: 'rounded',
      transition: '0.3s',
      color: 'systemBlueLight',
      '&:not(:last-child)': {
        mr: 'xs'
      },
      '&:hover': {
        color: 'veloShiftBlue'
      },
      '&:active, &.active': {
        color: 'veloWhite',
        backgroundColor: 'veloShiftBlue'
      }
    },
    tab: {
      variant: 'links.nav',
      color: 'text',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderWidth: 'borderless',
      borderBottomWidth: 'medium',
      borderRadius: 0,
      fontWeight: 'medium',
      pb: 's',
      '&:hover': {
        color: 'veloDarkBlueHover'
      },
      '&.active': {
        borderColor: 'veloShiftBlue',
        color: 'veloShiftBlue',
        '&:hover': {
          color: 'veloShiftBlue'
        }
      },
      '&:not(:last-child)': {
        mr: 'l'
      }
    },
    small: {
      variant: 'styles.a',
      fontSize: 'bodyS'
    }
  },
  text: {
    // Arbitrary override default text variant if you wish
    // default: {
    //   mb: 'xs'
    // },
    body: {
      fontFamily: 'body',
      fontSize: 'body',
      fontWeight: 'body',
      letterSpacing: 'body',
      lineHeight: 'body'
    },
    bodySmall: {
      variant: 'text.body',
      fontSize: 'bodyS',
      letterSpacing: 'bodyS',
      lineHeight: 'bodyS'
    },
    bodySmallSemibold: {
      variant: 'text.bodySmall',
      fontWeight: 'semibold'
    },
    bodyMedium: {
      variant: 'text.body',
      fontWeight: 'medium'
    },
    bodySemibold: {
      variant: 'text.body',
      fontWeight: 'semibold'
    },
    label: {
      variant: 'text.body',
      fontWeight: 'label',
      letterSpacing: 'label',
      lineHeight: 'label'
    },
    labelSemibold: {
      variant: 'text.bodySemibold',
      letterSpacing: 'label',
      lineHeight: 'label'
    },
    labelS: {
      variant: 'text.bodySemibold',
      fontSize: 'bodyS',
      letterSpacing: 'labelS',
      lineHeight: 'labelS',
      textTransform: 'uppercase',
      color: 'veloBlueGrey'
    },
    heading: {
      // You probably don't want to fix the fontSize of that one: it will depend of the "as" prop (to switch between h1, h2, h3 etc.)
      fontFamily: 'heading',
      fontWeight: 'bold',
      letterSpacing: 'heading',
      lineHeight: 'heading',
      color: 'veloMainBlue',
      my: 's'
    },
    heading1: {
      variant: 'text.heading',
      fontSize: 'heading1',
      lineHeight: 'heading1'
    },
    heading2: {
      variant: 'text.heading',
      fontSize: 'heading2',
      lineHeight: 'heading2'
    },
    heading3: {
      variant: 'text.heading',
      fontSize: 'heading3',
      lineHeight: 'heading3'
    },
    mega: {
      fontFamily: 'heading',
      fontSize: 'mega',
      fontWeight: 'mega',
      lineHeight: 'mega',
      letterSpacing: 'mega'
    }
  },
  // ------------------------------------------------------------ //
  // MDX styles                                                   //
  // Style rules applied to *MDX* components (not HTML elements!) //
  // https://theme-ui.com/styled                                  //
  // ------------------------------------------------------------ //
  styles: {
    root: {
      fontFamily: 'body',
      lineHeight: 'body',
      fontWeight: 'body'
    },
    h1: {
      color: 'text',
      fontFamily: 'heading',
      fontSize: 'heading1',
      fontWeight: 'bold'
    },
    h2: {
      color: 'text',
      fontFamily: 'heading',
      fontSize: 'heading2',
      fontWeight: 'bold'
    },
    h3: {
      color: 'text',
      fontFamily: 'heading',
      fontSize: 'heading3',
      fontWeight: 'bold'
    },
    h4: {
      color: 'text',
      fontFamily: 'heading',
      fontSize: 'heading3',
      fontWeight: 'bold'
    },
    h5: {
      color: 'text',
      fontFamily: 'heading',
      fontSize: 'heading3',
      fontWeight: 'bold'
    },
    h6: {
      color: 'text',
      fontFamily: 'heading',
      fontSize: 'heading3',
      fontWeight: 'bold'
    },
    p: {
      color: 'text',
      fontFamily: 'body',
      fontWeight: 'regular',
      lineHeight: 'body'
    },
    code: {
      fontFamily: 'monospace',
      fontSize: 'inherit'
    },
    pre: {
      fontFamily: 'monospace',
      overflowX: 'auto',
      code: {
        color: 'inherit'
      }
    },
    table: {
      borderCollapse: 'separate',
      borderSpacing: 0,
      width: '100%'
    },
    th: {
      borderBottomStyle: 'solid',
      textAlign: 'left'
    },
    td: {
      borderBottomStyle: 'solid',
      textAlign: 'left'
    },
    a: {
      variant: 'text.label',
      textDecoration: 'underline',
      color: 'veloDarkBlue',
      '&:hover': {
        color: 'veloDarkBlueHover'
      }
    },
    img: {
      maxWidth: '100%'
    },
    hr: {
      color: 'veloGrey3',
      my: 0
    }
  }
};

export default SchedulingTheme;
